import { useEffect, useRef, useState } from "react";
import { Button, type ButtonVariant } from "./Button";

interface CopyButtonProps {
  /** A thunk, not the raw string: the value (may be a one-time token) is read
   * only at click time, never held in this component's own state. */
  getValue: () => string;
  label?: string;
  variant?: ButtonVariant;
  className?: string;
}

/** Copies `getValue()` to the clipboard and shows a transient "Copied"
 * confirmation — never persists what it copied anywhere (no state beyond the
 * boolean "just copied" flag). */
export function CopyButton({ getValue, label = "Copy", variant = "secondary", className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  async function handleClick() {
    try {
      await navigator.clipboard.writeText(getValue());
      setCopied(true);
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied by the browser/OS; there's nothing
      // more to do than leave the value selectable for a manual copy.
    }
  }

  return (
    <Button type="button" variant={variant} className={className} onClick={() => void handleClick()}>
      {copied ? "Copied" : label}
    </Button>
  );
}
